import { useContext, useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import AnalyzeOrderVerify from "@/components/maps/AnalyzeOrderVerify"
import {
  OrderMetaData,
  StopMetaData,
} from "@/components/maps/AnalyzeOrderVerifyStops"
import AdHocCommands from "@/api/adHocCommands"
import Route2gFromFormParser from "@/lib/route2gFromFormParser"
import { parseRouteMetaData } from "@/hooks/useExtractDataFromOrder"
import { uploadToS3 } from "@/components/analyzeOrderOnPage/uploadToS3"
import { convertTextToImage } from "@/components/analyzeOrderOnPage/PlanOrder"
import { XmppContext } from "@/stream/xmppClient"
import { PlannedOrder } from "@/reducers/plannedOrdersSlice"
import { RouteStop } from "@/reducers/routes2gSlice"

export type PlannedOrderCreateMode = "file" | "text" | "manual"

type PlannedOrderCreateFlowProps = {
  mode: PlannedOrderCreateMode | null
  // Required for mode "file" — the picker runs in the pool panel so it opens
  // synchronously within the user gesture.
  initialFile?: File | null
  onExit: () => void
  onSaved: (order: PlannedOrder) => void
}

const EMPTY_ORDER_META: OrderMetaData = {
  ref_number: "",
  notes: "",
  weight: "",
  stops: [],
}

// Same StopMetaData -> RouteStop conversion as the on-page order flow
// (AnalyzeOrderOnPageModal), minus route generation.
const toRouteStops = (metaData: OrderMetaData): RouteStop[] =>
  (metaData.stops ?? []).map((stop: StopMetaData) => {
    const scheduledTimeMax = stop.scheduled_hour_max || "23:59:00"
    const scheduledDate = stop.scheduled_arrival_time
      ? stop.scheduled_arrival_time.split("T")[0]
      : undefined
    return {
      action: stop.action,
      id: uuidv4(),
      location: {
        formattedAddress:
          stop.place?.formattedAddress ?? stop.location?.formattedAddress,
        companyName: stop.location?.companyName,
        street: stop.location?.street,
        locality: stop.location?.locality,
        country: stop.location?.country,
        countryCode: stop.location?.countryCode,
        postalCode: stop.place?.addressComponents
          ? (stop.place.addressComponents.find((component) =>
              component.types.includes("postal_code"),
            )?.longText ?? stop.location?.postalCode)
          : stop.location?.postalCode,
        lat: stop.place?.location?.lat?.toString() ?? stop.location?.lat,
        lon: stop.place?.location?.lng?.toString() ?? stop.location?.lon,
      },
      action_reference_number: stop.action_reference_number,
      ref: metaData.ref_number,
      scheduled_hour_min: stop.scheduled_hour_min,
      scheduled_hour_max: stop.scheduled_hour_max,
      pallet_count: stop.pallet_count,
      weight_kg: stop.weight_kg,
      scheduled_arrival_time: scheduledDate
        ? `${scheduledDate}T${scheduledTimeMax}`
        : undefined,
    } as RouteStop
  })

const toPlannedOrder = (
  metaData: OrderMetaData,
  createdBy?: string,
): PlannedOrder => {
  // OrderMetaData minus stops is field-compatible with DeliveryOrderMetadata
  const { stops: _stops, ...orderMetadata } = metaData
  return {
    id: uuidv4(),
    stops: toRouteStops(metaData),
    metadata: orderMetadata,
    assignmentStatus: "unassigned",
    createdBy,
    createdAt: new Date().toISOString(),
  }
}

// The planner's "create unassigned order" flow: the exact first step of the
// add-transport-order experience (upload a PDF/image or paste text -> AI
// extraction -> fullscreen verify screen), but the CTA saves to the unassigned
// pool instead of generating a route for a driver.
const PlannedOrderCreateFlow = ({
  mode,
  initialFile,
  onExit,
  onSaved,
}: PlannedOrderCreateFlowProps) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)

  const [isVerifyOpen, setVerifyOpen] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [extractedData, setExtractedData] = useState<OrderMetaData | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [textContent, setTextContent] = useState("")
  const [isTextDialogOpen, setTextDialogOpen] = useState(false)

  const resetState = () => {
    setVerifyOpen(false)
    setIsProcessing(false)
    setIsFailed(false)
    setExtractedData(null)
    setFileName(null)
    setFileUrl(null)
    setTextContent("")
    setTextDialogOpen(false)
  }

  const exit = () => {
    resetState()
    onExit()
  }

  useEffect(() => {
    if (mode === "file" && initialFile) {
      void processFile(initialFile)
    } else if (mode === "text") {
      setTextDialogOpen(true)
    } else if (mode === "manual") {
      setExtractedData(EMPTY_ORDER_META)
      setVerifyOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  const processFile = async (file: File) => {
    if (!client || !myJid) {
      setIsFailed(true)
      return
    }

    setIsProcessing(true)
    setIsFailed(false)
    setVerifyOpen(true)
    setFileName(file.name)

    try {
      const { getUrl, fileName: s3FileName } = await uploadToS3(file)
      setFileUrl(getUrl)
      setFileName(s3FileName)

      const result = await AdHocCommands.extractRouteFromOrders(
        client,
        myJid.toString(),
        [{ url: getUrl, name: s3FileName, file }],
      )

      const commandResponse = result?.getChild("command")
      if (!commandResponse) {
        throw new Error("No command response from XMPP server")
      }
      const routeMetaData = parseRouteMetaData(commandResponse)
      const route2g = Route2gFromFormParser.parse(commandResponse)
      if (!routeMetaData || !route2g) {
        throw new Error("Failed to parse route data from response")
      }

      setExtractedData({
        ...routeMetaData,
        stops: route2g.stops as unknown as StopMetaData[],
      })
      window.analytics?.track("Planner_OrderMetaDataExtracted")
    } catch (error) {
      console.error("Error processing planned order document:", error)
      window.analytics?.track("Planner_OrderMetaDataExtractingFailed")
      setIsFailed(true)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleTextSubmit = async () => {
    if (!textContent.trim()) return
    setTextDialogOpen(false)
    const file = await convertTextToImage(textContent)
    await processFile(file)
  }

  const handleContinue = (metaData: OrderMetaData) => {
    onSaved(toPlannedOrder(metaData, myJid?.bare().toString()))
    exit()
  }

  if (!mode) return null

  return (
    <>
      <Dialog
        open={isTextDialogOpen}
        onOpenChange={(open) => {
          if (!open) exit()
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("planner.createFlow.textDialogTitle")}</DialogTitle>
          </DialogHeader>
          <Textarea
            value={textContent}
            onChange={(event) => setTextContent(event.target.value)}
            placeholder={t("planner.createFlow.textPlaceholder")}
            className="min-h-48"
          />
          <DialogFooter>
            <Button variant="outline" onClick={exit}>
              {t("planner.common.cancel")}
            </Button>
            <Button
              onClick={() => void handleTextSubmit()}
              disabled={!textContent.trim()}
            >
              {t("planner.createFlow.analyze")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isVerifyOpen && (
        <div className="fixed inset-0 z-50 bg-black/70">
          <div className="h-full w-full bg-white">
            <AnalyzeOrderVerify
              onCloseFullScreen={exit}
              stops={(extractedData?.stops ?? []) as unknown as RouteStop[]}
              routeMetaData={extractedData ?? EMPTY_ORDER_META}
              files={fileUrl ? [{ name: fileName ?? "", url: fileUrl }] : []}
              onContinue={handleContinue}
              isLoading={isProcessing}
              isFailed={isFailed}
              isFetchingPolylines={false}
              isFetchingPolylinesFailed={false}
              polylineErrors={null}
              onClearPolylineErrors={() => {}}
              isRouteFetched={false}
              onEdited={() => {}}
              onResetMessageGenerated={() => {}}
              showSkipStepButton={false}
              continueLabel={t("planner.createFlow.saveToPool")}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default PlannedOrderCreateFlow
